import randomstring from 'randomstring';

import { BridgeMessageBuilder } from './BridgeMessageBuilder';

import { ClassroomDto, MemberDto } from 'common/apollo/graphql';

const WEBVIEW_BRIDGE_TIMEOUT = 10000;

type TokenFromApp = {
  token_type: string;
  access_token: string;
  expires_in: string;
  state?: string;
};

type ClassTalkMessageObject = {
  category: string;
  type: 'open_chat_list' | 'enter_channel';
  callback?: string;
  payload?: ClassTalkPayload | undefined;
};

type CommonMessageObject = {
  category: string;
  type: 'change_config';
  payload?: {
    toolbar?: {
      isVisible: boolean;
    };
  };
};

type ClassTalkPayload = {
  creator: Creator;
  receiver: Receiver;
  classInfo: ClassInfo;
};

type Creator =
  | Pick<MemberDto, 'id' | 'name' | 'role' | 'profileImageUrl'>
  | null
  | undefined;

type Receiver =
  | Pick<MemberDto, 'id' | 'name' | 'role' | 'profileImageUrl'>
  | null
  | undefined;

type ClassInfo =
  | Pick<ClassroomDto, 'id' | 'name' | 'profileImageUrl'>
  | null
  | undefined;

export class WebViewBridge {
  private static request = (message: string): void => {
    if (window?.top?.ReactNativeWebView) {
      window.top.ReactNativeWebView?.postMessage(message);
    }
  };

  private static requestWithCallback<T>(message: string, callbackKey: string) {
    const cleanUp = () =>
      setTimeout(() => {
        if (window.top) {
          delete window.top.bridgeCallbacks?.[callbackKey];
          delete window.top.bridgeErrorCallbacks?.[callbackKey];
        }
      }, 500);

    const asyncRequest = new Promise<T>((resolve, reject) => {
      if (!window.top) {
        return null;
      }

      if (window.top.bridgeCallbacks === undefined) {
        window.top.bridgeCallbacks = {};
      }

      if (window.top.bridgeErrorCallbacks === undefined) {
        window.top.bridgeErrorCallbacks = {};
      }

      window.top.bridgeCallbacks[callbackKey] = function (
        stringifiedParam: string
      ): void {
        const data = JSON.parse(stringifiedParam);
        cleanUp();
        resolve(data);
      };

      window.top.bridgeErrorCallbacks[callbackKey] = function (
        errorMessage?: string
      ) {
        cleanUp();
        reject(new WebViewBridgeCallbackFailedError(errorMessage, message));
      };

      setTimeout(() => {
        if (
          window?.top?.bridgeCallbacks?.[callbackKey] ||
          window?.top?.bridgeErrorCallbacks?.[callbackKey]
        ) {
          cleanUp();
          reject(
            new WebViewBridgeCallbackFailedError(
              'webview bridge timeout.',
              message
            )
          );
        }
      }, WEBVIEW_BRIDGE_TIMEOUT);

      WebViewBridge.request(message);
    });

    return asyncRequest;
  }

  static authorize = (): Promise<TokenFromApp> => {
    const callbackKey = randomstring.generate();

    const message = new BridgeMessageBuilder()
      .setType('authorize')
      .setCallbackKey(callbackKey)
      .getBridgeMessage();

    return WebViewBridge.requestWithCallback<TokenFromApp>(
      message,
      callbackKey
    );
  };

  static open = (url: string): void => {
    const message = new BridgeMessageBuilder()
      .setType('open')
      .setUrl(url)
      .getBridgeMessage();

    WebViewBridge.request(message);
  };

  static close = (): void => {
    const message = new BridgeMessageBuilder()
      .setType('close')
      .getBridgeMessage();

    WebViewBridge.request(message);
  };

  static share = (sharedMessage: string): void => {
    const message = new BridgeMessageBuilder()
      .setType('share')
      .setMessage(sharedMessage)
      .getBridgeMessage();

    WebViewBridge.request(message);
  };

  static openClassTalkChatList = (): Promise<void> => {
    const callbackKey = randomstring.generate();

    const messageObject: ClassTalkMessageObject = {
      category: 'chat',
      type: 'open_chat_list',
      callback: callbackKey,
    };

    return WebViewBridge.requestWithCallback<void>(
      JSON.stringify(messageObject),
      callbackKey
    );
  };

  static enterClassTalkChannel = (
    creator: Creator,
    receiver: Receiver,
    classInfo: ClassInfo
  ): Promise<void> => {
    const callbackKey = randomstring.generate();

    const messageObject: ClassTalkMessageObject = {
      category: 'chat',
      type: 'enter_channel',
      callback: callbackKey,
      payload: {
        creator,
        receiver,
        classInfo,
      },
    };

    return WebViewBridge.requestWithCallback<void>(
      JSON.stringify(messageObject),
      callbackKey
    );
  };

  static showBottomNavigation = () => {
    if (WebViewBridge.isClasstingRNWebView()) {
      const message: CommonMessageObject = {
        category: 'webView',
        type: 'change_config',
        payload: {
          toolbar: {
            isVisible: true,
          },
        },
      };

      WebViewBridge.request(JSON.stringify(message));
    }
  };

  static hideBottomNavigation = () => {
    if (WebViewBridge.isClasstingRNWebView()) {
      const message: CommonMessageObject = {
        category: 'webView',
        type: 'change_config',
        payload: {
          toolbar: {
            isVisible: false,
          },
        },
      };

      WebViewBridge.request(JSON.stringify(message));
    }
  };

  static getClasstingRNAppVersion = (toNumber = false): string | number => {
    if (!window.top) {
      return '';
    }

    const { userAgent } = window.top.navigator;

    const regex = /classting_rn,\s(\d+).(\d+).(\d+)/;
    const match = regex.exec(userAgent.toLowerCase());

    if (toNumber) {
      return match ? Number(match.slice(1, 4).join('')) : 0;
    }

    return match ? match.slice(1, 4).join('.') : '';
  };

  static isClasstingRNWebView = (): boolean => {
    return window?.top?.navigator?.userAgent?.includes('classting_rn') ?? false;
  };
}

class WebViewBridgeCallbackFailedError extends Error {
  bridgeMessage?: string;

  constructor(message?: string, bridgeMessage?: string) {
    super(message ?? 'webview bridge callback execution failed');
    /**
     * TODO 추후 app 에서 serialize한 error 데이터가 넘어오면 deserialize 처리해야함
     *   labels: Squad: Comms, Frontend
     */
    this.name = 'WebViewBridgeCallbackFailedError';
    this.bridgeMessage = bridgeMessage;
  }
}
